/**
 * @fileoverview Page-margin field definitions + pure helpers.
 *
 * Split out of `PageMarginsPopover.tsx` (react-refresh/only-export-components)
 * — the field/zone layout, the wire-patch conversion, and the
 * has-custom-margins check, shared with its test suite.
 */

import type {
  DocumentPadding,
  PaddingSource,
} from "@/utils/branding/resolve-letterhead";

export type FieldKey =
  | "headerTopPadding"
  | "headerLeftPadding"
  | "headerRightPadding"
  | "bodyTopPadding"
  | "bodyLeftPadding"
  | "bodyRightPadding"
  | "bodyBottomPadding"
  | "footerBottomPadding"
  | "footerLeftPadding"
  | "footerRightPadding";

export const ZONES: Array<{
  zone: string;
  fields: Array<{
    key: FieldKey;
    label: string;
    resolved: keyof DocumentPadding;
  }>;
}> = [
  {
    zone: "Header",
    fields: [
      { key: "headerTopPadding", label: "Top", resolved: "headerTop" },
      { key: "headerLeftPadding", label: "Left", resolved: "headerLeft" },
      { key: "headerRightPadding", label: "Right", resolved: "headerRight" },
    ],
  },
  {
    zone: "Body",
    fields: [
      { key: "bodyTopPadding", label: "Top", resolved: "bodyTop" },
      { key: "bodyLeftPadding", label: "Left", resolved: "bodyLeft" },
      { key: "bodyRightPadding", label: "Right", resolved: "bodyRight" },
      { key: "bodyBottomPadding", label: "Bottom", resolved: "bodyBottom" },
    ],
  },
  {
    zone: "Footer",
    fields: [
      { key: "footerBottomPadding", label: "Bottom", resolved: "footerBottom" },
      { key: "footerLeftPadding", label: "Left", resolved: "footerLeft" },
      { key: "footerRightPadding", label: "Right", resolved: "footerRight" },
    ],
  },
];
export const ALL_FIELDS = ZONES.flatMap((z) => z.fields);

/** Null-free padding patch (what the Convex mutations accept). */
export type PageMarginPatch = Partial<Record<FieldKey, number>>;

export function toPatch(p: PaddingSource | null): PageMarginPatch | null {
  if (!p) return null;
  const out: PageMarginPatch = {};
  for (const f of ALL_FIELDS) {
    const v = p[f.key];
    if (v != null) out[f.key] = v;
  }
  return out;
}

export function pageHasCustomMargins(
  current: PaddingSource | null | undefined,
): boolean {
  return !!current && ALL_FIELDS.some((f) => current[f.key] != null);
}
